import React, {useCallback, useEffect, useRef} from "react";
import {memo} from "react";
import {RootState} from "../../../store/rootReducer";
import {useDispatch, useSelector} from "react-redux";
import {addCardActions} from "../addCard.slice";
import { styled } from "styled-components";
import { ThemeProps } from "../../../styles/theme";
import { useTheme } from "../../../styles/ThemeContext";
import { generateTagsFromMarkup } from "../utils/tagGenerator";

const selector = (state: RootState) => ({
    markup: state.addCard.markup,
});

const Root = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    position: relative;
`;

const Editor = styled.textarea<ThemeProps>`
    height: 290px;
    width: 100%;
    resize: none;
    margin: 0;
    padding: 10px;
    background-color: ${(props: ThemeProps) => props.theme.primaryBackground};
    color: ${(props: ThemeProps) => props.theme.inputText};
    border: ${(props: ThemeProps) => props.theme.primaryBorder} 1px solid;
    border-width: 1px 1px 2px 1px;
    outline: none;
    font-size: 14px;
    font-family: monospace;
    box-sizing: border-box;
    display: block;
`;

const MarkupEditorComponent: React.FC = memo(() => {
    const stateProps = useSelector(selector);
    const dispatch = useDispatch();
    const { theme } = useTheme();
    const editorRef = useRef<HTMLTextAreaElement>(null);
    const cursorPositionRef = useRef<number | null>(null);

    // Функция для обновления тегов на основе текущей разметки
    const updateTagsFromMarkup = useCallback((markup: string) => {
        // Получаем текущую разметку
        
        // Генерируем теги на основе вопроса и ответа
        const generatedTags = generateTagsFromMarkup(markup);
        
        // Если есть сгенерированные теги, добавляем их в разметку
        if (generatedTags.length > 0) {
            // Сохраняем текущую позицию курсора перед обновлением разметки
            if (editorRef.current) {
                cursorPositionRef.current = editorRef.current.selectionStart;
            }
            
            // Проверяем, есть ли в разметке маркер {tags}
            const tagsMarker = '{tags}';
            const tagsIndex = markup.indexOf(tagsMarker);
            
            let newMarkup: string;
            if (tagsIndex !== -1) {
                // Если маркер {tags} уже есть, заменяем теги после него
                const beforeTags = markup.substring(0, tagsIndex + tagsMarker.length);
                
                // Находим конец блока тегов (до следующего маркера или до конца текста)
                const nextMarkerIndex = markup.substring(tagsIndex + tagsMarker.length).search(/{[a-z-]+}/);
                const afterTagsIndex = nextMarkerIndex !== -1 
                    ? tagsIndex + tagsMarker.length + nextMarkerIndex 
                    : undefined;
                
                const afterTags = afterTagsIndex !== undefined 
                    ? markup.substring(afterTagsIndex) 
                    : '';
                
                newMarkup = beforeTags + '\n' + generatedTags.join(', ') + 
                    (afterTagsIndex !== undefined ? '\n' : '') + afterTags;
            } else {
                // Если маркера {tags} нет, добавляем его и теги в конец разметки
                newMarkup = markup + '\n\n{tags}\n' + generatedTags.join(', ');
            }
            
            // Обновляем разметку с новыми тегами
            dispatch(addCardActions.setMarkup(newMarkup));
        }
    }, [dispatch]);

    // Обработчик изменения текста
    const textChangedHandler = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newMarkup = event.target.value;
        dispatch(addCardActions.setMarkup(newMarkup));
    }, [dispatch]);
    
    // Обработчик потери фокуса - генерируем теги
    const blurHandler = useCallback(() => {
        updateTagsFromMarkup(stateProps.markup);
    }, [stateProps.markup, updateTagsFromMarkup]);

    // Восстанавливаем позицию курсора после обновления разметки
    useEffect(() => {
        if (editorRef.current && cursorPositionRef.current !== null) {
            editorRef.current.selectionStart = cursorPositionRef.current;
            editorRef.current.selectionEnd = cursorPositionRef.current;
            cursorPositionRef.current = null;
        }
    }, [stateProps.markup]);

    const pasteHandler = useCallback((event: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const position = (event.target as HTMLTextAreaElement).selectionStart;
        if (position === undefined)
            return;

        const items = event.clipboardData.items;
        if (items.length !== 1 || !items[0].type.startsWith('image/'))
            return;

        const blob = items[0].getAsFile();
        if (blob === null) return;
        
        const reader = new FileReader();
        reader.onload = function(readerEvent)
        {
            const dataUrl = readerEvent.target!.result!.toString();
            dispatch(addCardActions.addImage({
                cursorPosition: position,
                dataUrl,
            }))
        };
        reader.readAsDataURL(blob);
    }, [dispatch]);

    return (
        <Root>
            <Editor
                theme={theme}
                data-testid="card-markup-input"
                value={stateProps.markup}
                onChange={textChangedHandler}
                onBlur={blurHandler}
                onPaste={pasteHandler}
                ref={editorRef}
            />
        </Root>
    );
});

MarkupEditorComponent.displayName = 'MarkupEditorComponent';

export { MarkupEditorComponent as MarkupEditor };
